import React, { useState, useEffect, useContext } from 'react';
import gql from 'graphql-tag';
import { useQuery, useMutation } from '@apollo/react-hooks';
import { useLocation, matchPath } from 'react-router-dom';
import { AccountContext } from '../../../components/AccountContext';
import { NotificationContext } from '../../../components/GrowlNotifications';
import { Loading } from '../../../components/Loading';
import AddClientAccountForm from './AddClientAccountForm';
import apollo from '../../../services/apollo';
import { AccountType } from '../../../shared/Constants';

const getInvitationTypeFromPath = (path) => {
  const addClient = matchPath(path, {
    path: '/accounts/:aid/accounts/add-client',
    exact: true,
    strict: false,
  });

  const addCp = matchPath(path, {
    path: '/accounts/:aid/accounts/add-cp',
    exact: true,
    strict: false,
  });

  const addMcp = matchPath(path, {
    path: '/accounts/:aid/accounts/add-mcp',
    exact: true,
    strict: false,
  });

  const addNbmClient = matchPath(path, {
    path: '/accounts/:aid/accounts/add-nbm-client',
    exact: true,
    strict: false,
  });

  const addGp = matchPath(path, {
    path: '/accounts/:aid/accounts/add-gp',
    exact: true,
    strict: false,
  });

  const addGsc = matchPath(path, {
    path: '/accounts/:aid/accounts/add-gsc',
    exact: true,
    strict: false,
  });

  if (addClient) {
    return AccountType.CLIENT;
  }

  if (addCp) {
    return AccountType.CP;
  }

  if (addMcp) {
    return AccountType.MCP;
  }

  if (addNbmClient) {
    return AccountType.NBM_CLIENT;
  }

  if (addGp) {
    return AccountType.GP;
  }

  if (addGsc) {
    return AccountType.GSC;
  }

  throw Error('Unknown invitation type');
};

const CURRENT_ACCOUNT_QUERY = gql`
  query getCurrentAccount($accountId: ID!) {
    availableNumbersOfEmployees(id: $accountId)
    account(id: $accountId) {
      id
      type
    }
  }
`;

const ADD_CLIENT_QUERY = gql`
  query addClientQuery($partnerId: ID!) {
    activeLocales
    countries: availableCountries
    account(id: $partnerId) {
      id
      displayName
      type
      ownerBusinessCountries
      assessmentTemplates(first: 100) {
        edges {
          node {
            id
            title
          }
          cursor
        }
        totalCount
        indexFrom
        indexTo
        pageInfo {
          startCursor
          endCursor
          hasPreviousPage
          hasNextPage
        }
      }
      assessmentSections {
        behaviorSection {
          displayName
          dimensions {
            id
            name
            displayName
          }
          distortionDimensions {
            id
            name
            displayName
          }
        }
        cognitiveSection {
          displayName
          dimensions {
            id
            name
            displayName
          }
        }
        interestsSection {
          displayName
          dimensions {
            id
            name
            displayName
          }
        }
      }

      surveyTemplates(first: 100) {
        edges {
          node {
            id
            title
          }
          cursor
        }
        totalCount
        indexFrom
        indexTo
        pageInfo {
          startCursor
          endCursor
          hasPreviousPage
          hasNextPage
        }
      }

      surveyCompetencies {
        id
        displayName
        abilities {
          id
          displayName
        }
      }
    }
  }
`;

/**
 * Mutation to add a new client account.
 * Requires input of shape:
{
 "input": {
  "partnerId": 123,
  "email": "client-email@example.com",
  "fullName": "Happy Client Inc",
  "inviteMessage": "Join us!",
  "assessmentTemplateIds": [1,2,3],
  "assessmentDimensions": {
    "behaviorDimensions": [],
    "distortionDimensions": [],
    "cognitiveDimensions": [],
    "interestsDimensionsArePresent": true
  },
  "surveyTemplateIds": [1,3,4],
  "surveyAbilities": {}
}
}
 */
const ADD_CLIENT = gql`
  mutation createClientInvitation($input: CreateAccountInvitationInput!) {
    createClientInvitation(input: $input) {
      clientInvitation {
        id
      }
    }
  }
`;

const AddClientAccountContainer = (props) => {
  const [availableNumberOfEmployees, setAvailableNumberOfEmployees] = useState([]);
  const [currentAccountType, setCurrentAccountType] = useState(null);

  const { children, currentAccount } = props;

  const { loading, error, data } = useQuery(ADD_CLIENT_QUERY, {
    variables: {
      partnerId: currentAccount,
    },
  });

  const [addClientMutation] = useMutation(ADD_CLIENT);

  const updateStateAfterQuery = async () => {
    const result = await apollo.query({
      query: CURRENT_ACCOUNT_QUERY,
      variables: {
        accountId: currentAccount,
      },
    });

    if (result && result.data) {
      const { type } = result.data.account;
      const numberOfEmployees = result.data.availableNumbersOfEmployees;

      setCurrentAccountType(type);
      setAvailableNumberOfEmployees(numberOfEmployees);
    }
  };

  useEffect(() => {
    updateStateAfterQuery();
  });

  return children({
    invitation: { loading, data, error },
    addClient: addClientMutation,
    currentAccountType,
    availableNumberOfEmployees,
  });
};

const AddClientAccount = () => {
  const currentAccount = useContext(AccountContext);
  const growl = useContext(NotificationContext);
  const { pathname } = useLocation();

  return (
    <AddClientAccountContainer currentAccount={currentAccount}>
      {({ invitation: { loading, error, data }, addClient, currentAccountType, availableNumberOfEmployees }) => {
        if (error) {
          growl.show({ severity: 'error', sticky: true, detail: error.message });
        }

        if (loading || !data) {
          return <Loading />;
        }

        const type = getInvitationTypeFromPath(pathname);

        return (
          <AddClientAccountForm
            data={data}
            addClient={addClient}
            type={type}
            currentAccountType={currentAccountType}
            availableNumberOfEmployees={availableNumberOfEmployees}
          />
        );
      }}
    </AddClientAccountContainer>
  );
};

export default AddClientAccount;
